import { useCallback, useEffect, useMemo, useState } from "react"

import { Abas, PainelDaAba, type DescricaoDeAba } from "../componentes/Abas"
import { PainelComSubmenu, type ItemDeSubmenu } from "../componentes/PainelComSubmenu"
import { DatasDoContrato } from "./DatasDoContrato"
import { PecasDoProcesso } from "./PecasDoProcesso"
import * as Icones from "../componentes/Icones"
import { Vigencia } from "./Vigencia"
import { CabecaDaFicha } from "../ficha/Cabeca"
import { BlocoContratado, BlocoDesignados, BlocoIdentificacao, BlocoObjeto } from "../ficha/Blocos"
import { BlocoEditavel } from "../ficha/Campos"
import { BlocoAtestes, BlocoEmpenhos, BlocoMedicoes, BlocoPagamentos } from "../ficha/Execucao"
import { BlocoConferencia, BlocoOcorrencias, Regularidade } from "../ficha/Fiscalizacao"
import { BlocoAditivos, BlocoApostilamentos } from "../ficha/Alteracoes"
import { BlocoDotacao, BlocoGarantias } from "../ficha/Contrato"
import { BlocoLaudos } from "../ficha/Processo"
import { EsqueletoDaFicha, usePedido } from "../ficha/Pedido"
import { FalhaNaSecao } from "../componentes/Secao"
import { Button } from "../componentes/Button"
import { SemDicaNosSelos } from "../componentes/Selo"
import { Card, CardContent } from "../componentes/Card"
import { api, type Ficha as FichaDoContrato } from "../api"
import { BOTAO_BRANCO, CARTAO } from "../lib/classes"

export type AbaDaFicha =
  | "contrato"
  | "vigencia"
  | "processo"
  | "execucao"
  | "alteracoes"
  | "fiscalizacao"

export const ABA_INICIAL: AbaDaFicha = "contrato"

export type SubmenuDaFicha = Partial<Record<AbaDaFicha, string>>

const SUBMENU: Record<AbaDaFicha, [string, ...string[]]> = {
  contrato: ["identificacao", "objeto", "contratado", "dotacao", "garantias"],
  vigencia: ["datas", "memoria", "providencias", "historico"],
  processo: ["pecas", "esteira", "laudos"],
  execucao: ["medicoes", "atestes", "empenhos", "pagamentos"],
  alteracoes: ["aditivos", "apostilamentos", "reajustes"],
  fiscalizacao: [
    "designados",
    "conferencia",
    "ocorrencias",
    "regularidade",
  ],
}

function descreverAbas(ficha: FichaDoContrato | null): DescricaoDeAba<AbaDaFicha>[] {
  const atencao = ficha?.atencao
  const seHouver = (valor: number | undefined) => (valor === undefined || valor === 0 ? null : valor)
  return [
    { chave: "contrato", rotulo: "Contrato" },
    { chave: "vigencia", rotulo: "Vigência e prazos", contagem: seHouver(atencao?.providencias_atrasadas) },
    { chave: "processo", rotulo: "Processo" },
    { chave: "execucao", rotulo: "Execução" },
    { chave: "alteracoes", rotulo: "Alterações" },
    {
      chave: "fiscalizacao",
      rotulo: "Fiscalização",
      contagem: seHouver(atencao === undefined ? undefined : atencao.conferencia_pendente + atencao.certidoes_vencidas),
    },
  ]
}

type Props = {
  contratoId: number
  aoVoltar: () => void
  aoAbrirProcesso: (pecaId?: number) => void
  aba: AbaDaFicha
  aoTrocarAba: (aba: AbaDaFicha) => void
  submenu: SubmenuDaFicha
  aoTrocarSubmenu: (aba: AbaDaFicha, item: string) => void
  aoSaberDoProcesso?: (processo: string | null) => void
}

export function Ficha({
  contratoId,
  aoVoltar,
  aoAbrirProcesso,
  aba,
  aoTrocarAba,
  submenu,
  aoTrocarSubmenu,
  aoSaberDoProcesso,
}: Props) {
  const pedidoDaFicha = usePedido(() => api.ficha(contratoId), `ficha-${contratoId}`)
  const ficha = pedidoDaFicha.dados
  const definirFicha = pedidoDaFicha.definir

  const [pecasNoProcesso, definirPecasNoProcesso] = useState<number | null>(null)
  const [pedidoDeGaveta, definirPedidoDeGaveta] = useState<{ qual: "ocorrencias" | "designados"; vez: number } | null>(null)
  const pedidoPara = (qual: "ocorrencias" | "designados") => (pedidoDeGaveta?.qual === qual ? pedidoDeGaveta.vez : 0)

  const [abertas, definirAbertas] = useState<Set<AbaDaFicha>>(() => new Set([aba]))
  const [abertos, definirAbertos] = useState<Record<string, Set<string>>>(() => ({
    [aba]: new Set([submenu[aba] !== undefined && SUBMENU[aba].includes(submenu[aba]) ? submenu[aba] : SUBMENU[aba][0]]),
  }))

  const itemDaAba = useCallback(
    (qual: AbaDaFicha) => {
      const pedido = submenu[qual]
      return pedido !== undefined && SUBMENU[qual].includes(pedido) ? pedido : SUBMENU[qual][0]
    },
    [submenu],
  )

  useEffect(() => {
    definirAbertas((anteriores) =>
      anteriores.has(aba) ? anteriores : new Set(anteriores).add(aba),
    )
  }, [aba])

  const itemAtual = itemDaAba(aba)
  useEffect(() => {
    definirAbertos((anteriores) => {
      const desta = anteriores[aba] ?? new Set<string>()
      if (desta.has(itemAtual)) return anteriores
      return { ...anteriores, [aba]: new Set(desta).add(itemAtual) }
    })
  }, [aba, itemAtual])

  useEffect(() => {
    definirPecasNoProcesso(null)
  }, [contratoId])

  const pedidoDaVigencia = usePedido(
    () => api.vigencia(contratoId),
    `vigencia-${contratoId}`,
    abertas.has("vigencia"),
  )
  const vigencia = pedidoDaVigencia.dados
  const definirVigencia = pedidoDaVigencia.definir

  const laudosAbertos = (abertos["processo"] ?? new Set()).has("laudos")
  const pedidoDosLaudos = usePedido(() => api.laudosDoContrato(contratoId), `laudos-${contratoId}`, laudosAbertos)

  const processoDoContrato = ficha?.processo_administrativo ?? null
  useEffect(() => {
    aoSaberDoProcesso?.(processoDoContrato)
  }, [aoSaberDoProcesso, processoDoContrato])

  const abas = useMemo(
    () => descreverAbas(ficha),
    [ficha],
  )

  if (pedidoDaFicha.erro !== null) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <FalhaNaSecao
            mensagem={`Não foi possível abrir o contrato: ${pedidoDaFicha.erro}`}
            aoTentar={pedidoDaFicha.recarregar}
          />
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={aoVoltar}>
            <Icones.Voltar />
            Voltar
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (ficha === null) {
    return <EsqueletoDaFicha />
  }

  const atualizarAtencao = () => {
    api.ficha(contratoId).then(definirFicha).catch(() => undefined)
  }
  const aoAtualizarVigencia = (nova: Parameters<typeof definirVigencia>[0]) => {
    definirVigencia(nova)
    atualizarAtencao()
  }

  const irPara = (destino: AbaDaFicha, item: string) => {
    aoTrocarAba(destino)
    aoTrocarSubmenu(destino, item)
  }

  const painel = (qual: AbaDaFicha, itens: ItemDeSubmenu<string>[]) => (
    <PainelComSubmenu
      nome={qual}
      rotulo={`Blocos de ${qual}`}
      itens={itens}
      atual={itemDaAba(qual)}
      aoTrocar={(item) => aoTrocarSubmenu(qual, item)}
      abertos={abertos[qual] ?? new Set([itemDaAba(qual)])}
    />
  )

  const reajustes = ficha.apostilamentos.filter(
    (a) => a.tipo === "reajuste" || a.tipo === "correcao_monetaria",
  )
  const demaisApostilamentos = ficha.apostilamentos.filter(
    (a) => a.tipo !== "reajuste" && a.tipo !== "correcao_monetaria",
  )
  const atestadas = ficha.medicoes.filter((m) => m.atestada_em !== null)

  return (
    <SemDicaNosSelos>
      <CabecaDaFicha
        ficha={ficha}
        aoAbrirProcesso={() => aoAbrirProcesso()}
        aoIrParaDesignados={() => {
          irPara("fiscalizacao", "designados")
          definirPedidoDeGaveta({ qual: "designados", vez: Date.now() })
        }}
        aoIrParaOcorrencias={() => {
          irPara("fiscalizacao", "ocorrencias")
          definirPedidoDeGaveta({ qual: "ocorrencias", vez: Date.now() })
        }}
      />
      <div className={CARTAO}>
        <Abas
          nome="ficha"
          classe="mb-0 px-3 pt-3"
          rotulo="O que ver deste contrato"
          abas={abas}
          atual={aba}
          aoTrocar={aoTrocarAba}
        />

        {abertas.has("contrato") && (
          <PainelDaAba nome="ficha" chave="contrato" ativa={aba === "contrato"}>
            {painel("contrato", [
              {
                chave: "identificacao",
                rotulo: "Identificação",
                desenhar: () => (
                  <BlocoIdentificacao ficha={ficha} aoAtualizar={definirFicha} />
                ),
              },
              {
                chave: "objeto",
                rotulo: "Objeto e itens",
                contagem: ficha.itens.length,
                desenhar: () => <BlocoObjeto ficha={ficha} aoAtualizar={definirFicha} />,
              },
              {
                chave: "contratado",
                rotulo: "Contratado",
                desenhar: () => (
                  <BlocoContratado ficha={ficha} aoAtualizar={definirFicha} />
                ),
              },
              {
                chave: "dotacao",
                rotulo: "Dotação",
                contagem: ficha.dotacoes.length,
                desenhar: () => <BlocoDotacao ficha={ficha} />,
              },
              {
                chave: "garantias",
                rotulo: "Garantias",
                contagem: ficha.garantias.length,
                desenhar: () => <BlocoGarantias ficha={ficha} />,
              },
            ])}
          </PainelDaAba>
        )}

        {abertas.has("vigencia") && (
          <PainelDaAba nome="ficha" chave="vigencia" ativa={aba === "vigencia"}>
            {painel("vigencia", [
              {
                chave: "datas",
                rotulo: "Datas",
                desenhar: () => (
                  <DatasDoContrato
                    contratoId={contratoId}
                    aoAbrirProcesso={aoAbrirProcesso}
                    aoMudarDatas={() => {
                      api.vigencia(contratoId).then(definirVigencia).catch(() => undefined)
                      api.ficha(contratoId).then(definirFicha).catch(() => undefined)
                    }}
                  />
                ),
              },
              {
                chave: "memoria",
                rotulo: "Memória de vigência",
                desenhar: () => (
                  <Vigencia
                    contratoId={contratoId}
                    bloco="memoria"
                    estado={vigencia}
                    erro={pedidoDaVigencia.erro}
                    aoTentar={pedidoDaVigencia.recarregar}
                    aoAtualizar={aoAtualizarVigencia}
                  />
                ),
              },
              {
                chave: "providencias",
                rotulo: "Providências",
                contagem: vigencia?.providencias.length ?? null,
                desenhar: () => (
                  <Vigencia
                    contratoId={contratoId}
                    bloco="providencias"
                    estado={vigencia}
                    erro={pedidoDaVigencia.erro}
                    aoTentar={pedidoDaVigencia.recarregar}
                    aoAtualizar={aoAtualizarVigencia}
                  />
                ),
              },
              {
                chave: "historico",
                rotulo: "Histórico",
                contagem:
                  vigencia === null ? null : vigencia.historico.length + vigencia.declaracoes.length,
                desenhar: () => (
                  <Vigencia
                    contratoId={contratoId}
                    bloco="historico"
                    estado={vigencia}
                    erro={pedidoDaVigencia.erro}
                    aoTentar={pedidoDaVigencia.recarregar}
                    aoAtualizar={aoAtualizarVigencia}
                  />
                ),
              },
            ])}
          </PainelDaAba>
        )}

        {abertas.has("processo") && (
          <PainelDaAba nome="ficha" chave="processo" ativa={aba === "processo"}>
            {painel("processo", [
              {
                chave: "pecas",
                rotulo: "Peças por papel",
                contagem: pecasNoProcesso,
                desenhar: () => (
                  <PecasDoProcesso
                    contratoId={contratoId}
                    aoAbrirProcesso={aoAbrirProcesso}
                    aoContarNoProcesso={definirPecasNoProcesso}
                  />
                ),
              },
              {
                chave: "esteira",
                rotulo: "Esteira do processo",
                desenhar: () => (
                  <BlocoEditavel
                    titulo="Esteira do processo"
                    editavel={false}
                    motivoSemEdicao="a esteira é leitura do processo"
                    recado={
                      "O processo inteiro, na ordem em que foi montado, com o " +
                      "documento aberto ao lado da lista."
                    }
                    leitura={
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => aoAbrirProcesso()}
                      >
                        <Icones.Abrir />
                        Abrir o processo inteiro
                      </Button>
                    }
                  />
                ),
              },
              {
                chave: "laudos",
                rotulo: "Laudos da fiscalização",
                contagem: pedidoDosLaudos.dados === null ? null : pedidoDosLaudos.dados.length,
                desenhar: () => <BlocoLaudos contratoId={contratoId} pedido={pedidoDosLaudos} />,
              },
            ])}
          </PainelDaAba>
        )}

        {abertas.has("execucao") && (
          <PainelDaAba nome="ficha" chave="execucao" ativa={aba === "execucao"}>
            {painel("execucao", [
              {
                chave: "medicoes",
                rotulo: "Medições",
                contagem: ficha.medicoes.length,
                desenhar: () => <BlocoMedicoes ficha={ficha} />,
              },
              {
                chave: "atestes",
                rotulo: "Atestes",
                contagem: atestadas.length,
                desenhar: () => <BlocoAtestes ficha={ficha} />,
              },
              {
                chave: "empenhos",
                rotulo: "Empenhos",
                contagem: ficha.empenhos.length,
                desenhar: () => <BlocoEmpenhos ficha={ficha} />,
              },
              {
                chave: "pagamentos",
                rotulo: "Pagamentos",
                contagem: ficha.pagamentos.length,
                desenhar: () => <BlocoPagamentos ficha={ficha} />,
              },
            ])}
          </PainelDaAba>
        )}

        {abertas.has("alteracoes") && (
          <PainelDaAba nome="ficha" chave="alteracoes" ativa={aba === "alteracoes"}>
            {painel("alteracoes", [
              {
                chave: "aditivos",
                rotulo: "Aditivos",
                contagem: ficha.aditivos.length,
                desenhar: () => <BlocoAditivos ficha={ficha} />,
              },
              {
                chave: "apostilamentos",
                rotulo: "Apostilamentos",
                contagem: demaisApostilamentos.length,
                desenhar: () => (
                  <BlocoApostilamentos
                    linhas={demaisApostilamentos}
                    titulo="Apostilamentos"
                    vazio={
                      "Nenhum apostilamento de dotação ou de outra espécie. " +
                      "Reajuste e correção monetária ficam no bloco Reajustes."
                    }
                    nota="Registram o que não altera o ajuste. NÃO integram o limite de acréscimo do art. 125."
                  />
                ),
              },
              {
                chave: "reajustes",
                rotulo: "Reajustes",
                contagem: reajustes.length,
                desenhar: () => (
                  <BlocoApostilamentos
                    linhas={reajustes}
                    titulo="Reajustes"
                    vazio={
                      "Nenhum reajuste registrado."
                    }
                    nota="Reajuste e correção monetária, com o índice aplicado."
                  />
                ),
              },
            ])}
          </PainelDaAba>
        )}

        {abertas.has("fiscalizacao") && (
          <PainelDaAba nome="ficha" chave="fiscalizacao" ativa={aba === "fiscalizacao"}>
            {painel("fiscalizacao", [
              {
                chave: "designados",
                rotulo: "Designados",
                contagem: ficha.fiscais.filter((f) => f.vigente).length,
                desenhar: () => (
                  <BlocoDesignados
                    ficha={ficha}
                    aoAtualizar={definirFicha}
                    pedidoDeEdicao={pedidoPara("designados")}
                    aoAtenderPedido={() => definirPedidoDeGaveta(null)}
                  />
                ),
              },
              {
                chave: "conferencia",
                rotulo: "Conferência mensal",
                desenhar: () => <BlocoConferencia contratoId={contratoId} aoMudar={atualizarAtencao} />,
              },
              {
                chave: "ocorrencias",
                rotulo: "Ocorrências",
                contagem: ficha.ocorrencias.length,
                desenhar: () => (
                  <BlocoOcorrencias
                    ficha={ficha}
                    aoAtualizar={definirFicha}
                    pedidoDeEdicao={pedidoPara("ocorrencias")}
                    aoAtenderPedido={() => definirPedidoDeGaveta(null)}
                  />
                ),
              },
              {
                chave: "regularidade",
                rotulo: "Regularidade",
                desenhar: () => <Regularidade contratoId={contratoId} aoMudar={atualizarAtencao} />,
              },
            ])}
          </PainelDaAba>
        )}
      </div>
    </SemDicaNosSelos>
  )
}
